import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { Check, ChevronDown } from 'lucide-react';
import { ToolContextDataService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { useAuth } from '@/entities/user';
import type { ChatOptionsProps } from '../ChatOptions.types';
import { useOptionsManager } from '../hooks/useOptionsManager';
import { persistTemplatesSettingsFromStore } from '../../../model/useChatSettingsPersistence';
import { DEFAULT_SELECTABLE_MODEL, useAgentModelOptions } from '../hooks/useAgentModelOptions';
import { type TemplatesAgentOptions as TemplatesAgentOptionsType } from './TemplatesAgentOptions.types';
import type { Option } from '@/shared/ui/DropdownSelect/DropdownSelect';
import { useCurrentWorkspaceId } from '@/entities';
import { useCampaignFiles } from './useCampaignFiles';
import { useTranslation } from 'react-i18next';
import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';

const TEMPLATE_TOOL_ID = 'get_template_v2_tool';
const CREATOR_AGENT_ID = 'templates-creator-agent';

/**
 * Spreads `options` first so sibling keys owned by other components
 * (e.g. `llmOptions.*` written by the AI Model Selector in ChatInputV2)
 * are preserved across popup updates.
 */
function toStoreOptions(options: TemplatesAgentOptionsType) {
  return {
    ...options,
    llmOptions: options.model ? { model: options.model } : undefined,
  };
}

interface TemplateItem {
  templateName: string;
  slug: string;
  html: string;
}

export function TemplatesAgentOptions({ onOptionUpdated }: ChatOptionsProps) {
  const { t } = useTranslation('chats');

  const organizationId = useAuth((s) => s.signedInUser.value?.organization.id ?? '');
  const workspaceId = useCurrentWorkspaceId();

  const filteredModels = useAgentModelOptions();
  const defaultOptions = useMemo<TemplatesAgentOptionsType>(() => {
    const firstModel = filteredModels[0]?.value ?? DEFAULT_SELECTABLE_MODEL;
    return { model: firstModel };
  }, [filteredModels]);

  const { options, setOptions } = useOptionsManager({
    defaultOptions,
    onOptionUpdated,
    toStore: toStoreOptions,
  });

  // Persist ONLY on a user-driven change (not the template-default / stale-campaign / init effects,
  // which call `setOptions` directly). Zustand `set` is synchronous, so reading the store right after
  // `setOptions` sees the new value.
  const setAndPersist = useCallback(
    (updates: Partial<TemplatesAgentOptionsType>) => {
      setOptions(updates);
      persistTemplatesSettingsFromStore();
    },
    [setOptions],
  );

  const { data: templateData } = useQuery({
    queryKey: ['templates', 'templates', organizationId, workspaceId],
    queryFn: async () => {
      const { data } = await ToolContextDataService.findByToolId({
        client: inferenceHttp,
        path: { toolId: TEMPLATE_TOOL_ID },
        query: { organizationId, workspaceId: workspaceId ?? undefined, agentId: CREATOR_AGENT_ID },
      });
      return data ?? null;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  const templateOptions = useMemo(() => {
    const templates = (templateData?.data as { templates?: TemplateItem[] } | undefined)?.templates;
    return (templates ?? [])
      .map((t) => ({ value: t.slug, label: t.templateName }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [templateData]);

  useEffect(() => {
    if (templateOptions.length > 0 && !options.selectedTemplate) {
      setOptions({ selectedTemplate: templateOptions[0].value });
    }
  }, [templateOptions, options.selectedTemplate, setOptions]);

  // Campaign files come from the currently-selected knowledge bases.
  const knowledgeBaseIds = useMemo(
    () => options.knowledgeBaseIds ?? [],
    [options.knowledgeBaseIds],
  );
  const { campaignFiles, isLoading: campaignsLoading } = useCampaignFiles(knowledgeBaseIds);

  const campaignDropdownOptions = useMemo(
    () => [
      { value: '', label: t('chats.templatesAgentOptions.noCampaign') },
      ...campaignFiles.map((f) => ({ value: f.fileId, label: f.fileName })),
    ],
    [campaignFiles, t],
  );

  // Clear a stale campaign selection once the query settles and the file is no longer available
  // (e.g. the user changed knowledge bases or the file was removed).
  useEffect(() => {
    if (campaignsLoading) return;
    if (
      options.selectedCampaignId &&
      !campaignFiles.some((f) => f.fileId === options.selectedCampaignId)
    ) {
      setOptions({ selectedCampaignId: undefined });
    }
  }, [campaignsLoading, campaignFiles, options.selectedCampaignId, setOptions]);

  const templateDataRef = useRef(templateData);
  templateDataRef.current = templateData;

  const renderTemplatePreview = useCallback(
    (option: Option<string>) => {
      if (!option.value) return null;
      const templates = (
        templateDataRef.current?.data as { templates?: TemplateItem[] } | undefined
      )?.templates;
      const template = templates?.find((item) => item.slug === option.value);
      if (!template?.html) return null;
      return (
        <iframe
          srcDoc={template.html}
          sandbox=""
          title={t('chats.templatesAgentOptions.preview', { templateName: template.templateName })}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      );
    },
    [t],
  );

  const hasTemplates = templateOptions.length > 0;

  return (
    <div className="flex flex-col gap-4 w-80">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('chats.templatesAgentOptions.templatesAgentSettings')}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.templatesAgentOptions.template')}
        </span>
        <TemplateSelect
          label={t('chats.templatesAgentOptions.chooseTemplate')}
          value={options.selectedTemplate}
          onChange={(value) => setAndPersist({ selectedTemplate: value })}
          options={templateOptions}
          disabled={!hasTemplates}
          renderPreview={renderTemplatePreview}
          emptyMessage={t('chats.templatesAgentOptions.noTemplatesAvailable')}
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.templatesAgentOptions.campaign')}
        </span>
        <TemplateSelect
          label={t('chats.templatesAgentOptions.noCampaign')}
          value={options.selectedCampaignId ?? ''}
          onChange={(value) => setAndPersist({ selectedCampaignId: value || undefined })}
          options={campaignDropdownOptions}
          disabled={campaignFiles.length === 0}
          emptyMessage={t('chats.templatesAgentOptions.noCampaignsAvailable')}
        />
      </div>

      <Separator />

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-foreground cursor-help">
                  {t('chats.templatesAgentOptions.autoQaApply')}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="w-64 max-w-64">
                {t('chats.templatesAgentOptions.runQaAgainstYourSelected')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch
            checked={options.autoApplyQa ?? false}
            onCheckedChange={(checked) => setAndPersist({ autoApplyQa: checked })}
            disabled={!hasTemplates}
            className="data-[state=checked]:bg-foreground"
          />
        </div>
        {options.autoApplyQa && (options.selectedGuidelineIds?.length ?? 0) === 0 && (
          <span className="text-xs text-foreground-subtle">
            {t('chats.templatesAgentOptions.selectAtLeastOneGuideline')}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V2 local widget. Uses Radix Popover (not DropdownMenu) because this renders
// inside the ChatInputV2 settings DropdownMenu, and nesting DropdownMenu in
// DropdownMenu fights for focus/dismissal.
// ---------------------------------------------------------------------------

type TemplateSelectProps = {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: ReadonlyArray<Option<string>>;
  disabled?: boolean;
  renderPreview?: (option: Option<string>) => React.ReactNode;
  emptyMessage?: string;
};

const HOVER_DELAY_MS = 300;
const PREVIEW_COLLISION_PADDING = 16;

const TemplateSelect = ({
  label,
  value,
  onChange,
  options,
  disabled,
  renderPreview,
  // eslint-disable-next-line i18next/no-literal-string -- dead default: both call sites of this file-local component pass `emptyMessage`
  emptyMessage = 'No options available',
}: TemplateSelectProps) => {
  const labelByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);
  const selectedLabel = value ? labelByValue.get(value) : undefined;
  const { setNode: setSelectedLabelNode, isTruncated: isSelectedLabelTruncated } = useIsTruncated();
  const [isSelectedLabelTooltipOpen, setSelectedLabelTooltipOpen] = useState(false);

  const [hoveredOption, setHoveredOption] = useState<Option<string> | null>(null);
  const previewCacheRef = useRef<Set<string>>(new Set());
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const cancelHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const scheduleClearHover = () => {
    cancelHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredOption(null);
    }, HOVER_DELAY_MS);
  };

  const handleItemMouseEnter = (option: Option<string>) => {
    cancelHoverTimeout();
    previewCacheRef.current.add(option.value);
    setHoveredOption(option);
  };

  const handleItemMouseLeave = () => {
    if (renderPreview) scheduleClearHover();
  };

  const handlePreviewMouseEnter = () => cancelHoverTimeout();
  const handlePreviewMouseLeave = () => scheduleClearHover();

  // Recomputed each render so newly hovered items show up in the cached list
  // (previewCacheRef is a ref and would not trigger a useMemo re-run on its own).
  const cachedPreviewOptions = renderPreview
    ? options.filter((o) => previewCacheRef.current.has(o.value))
    : [];

  const triggerButton = (
    <button
      type="button"
      disabled={disabled}
      className="relative flex items-center gap-2 w-full min-h-9 px-3 py-1.5 rounded-lg border border-border bg-card text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex min-w-0 flex-1 items-center pr-5">
        {selectedLabel ? (
          <span ref={setSelectedLabelNode} className="truncate text-foreground text-sm">
            {selectedLabel}
          </span>
        ) : (
          <span className="text-foreground-subtle text-xs">{label}</span>
        )}
      </div>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-subtle" />
    </button>
  );

  // `PopoverTrigger` has to stay outside `TooltipTrigger`: both write `data-state` onto the
  // button, and `PopoverTrigger` spreads the props it receives after its own, so the tooltip's
  // state would win and the popover would read as closed while open.
  //
  // The wrappers stay mounted whether or not the label overflows, and the tooltip is held shut
  // through `open` instead. Dropping them when it fits would change the element type at this
  // position, so a resize that flips truncation would remount the button under a focused
  // keyboard user and throw their focus to the body.
  const selectTrigger = (
    <Tooltip
      open={!!selectedLabel && isSelectedLabelTruncated && isSelectedLabelTooltipOpen}
      onOpenChange={setSelectedLabelTooltipOpen}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
      </PopoverTrigger>
      <TooltipContent className="max-w-sm break-words">{selectedLabel}</TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={HOVER_DELAY_MS}>
      <Popover
        onOpenChange={(open) => {
          if (!open) {
            cancelHoverTimeout();
            setHoveredOption(null);
          }
        }}
      >
        {selectTrigger}
        {/*
        Width lives here rather than on the settings popover: this list popover is portaled
        to `document.body`, so the settings popover cannot size to campaign names it does not
        contain. `w-max` reads the longest name; the cap keeps a 166-character one to three
        wrapped lines. Radix only shifts a colliding popover, it never shrinks one, so the cap
        also has to yield to the available width or the far edge leaves the viewport below
        ~600px. `var(...)` must be spelled out: Tailwind v4 does not expand `(--x)` inside a
        bracketed value.
      */}
        <PopoverContent
          align="start"
          sideOffset={4}
          collisionPadding={PREVIEW_COLLISION_PADDING}
          className="p-1 w-max min-w-(--radix-popover-trigger-width) max-w-[min(560px,var(--radix-popover-content-available-width))]"
        >
          <div className="max-h-64 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-2 py-4 text-center text-xs text-foreground-subtle">
                {emptyMessage}
              </div>
            ) : (
              options.map((option) => {
                const selected = option.value === value;
                const optionRow = (
                  <TemplateSelectOption
                    option={option}
                    selected={selected}
                    anchored={!!renderPreview}
                    onSelect={() => onChange(option.value)}
                    onMouseEnter={renderPreview ? () => handleItemMouseEnter(option) : undefined}
                    onMouseLeave={renderPreview ? handleItemMouseLeave : undefined}
                  />
                );

                if (!renderPreview) return <div key={option.value}>{optionRow}</div>;

                const isHovered = hoveredOption?.value === option.value;
                return (
                  <Popover key={option.value} open={isHovered}>
                    {optionRow}
                    {isHovered && (
                      <PopoverContent
                        side="right"
                        align="start"
                        sideOffset={8}
                        collisionPadding={PREVIEW_COLLISION_PADDING}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onFocusOutside={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                        onMouseEnter={handlePreviewMouseEnter}
                        onMouseLeave={handlePreviewMouseLeave}
                        className="w-[480px] h-[420px] p-0 overflow-hidden rounded-md border border-border bg-card shadow-md"
                      >
                        {cachedPreviewOptions.map((cached) => (
                          <div
                            key={cached.value}
                            style={{
                              width: '100%',
                              height: '100%',
                              display: cached.value === option.value ? 'block' : 'none',
                            }}
                          >
                            {renderPreview(cached)}
                          </div>
                        ))}
                      </PopoverContent>
                    )}
                  </Popover>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};

// One row. Split out because `useIsTruncated` has to run once per option, which a hook called
// inside `options.map()` cannot do. Mirrors `BreadcrumbsLink` in `shared/ui/Breadcrumbs`: the
// overflow ref goes on the label, and the tooltip trigger goes on the focusable button, so the
// full name is reachable by keyboard and not only by pointer.
type TemplateSelectOptionProps = {
  option: Option<string>;
  selected: boolean;
  /** Anchors the hover preview to this row; only the template picker passes it. */
  anchored: boolean;
  onSelect: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

function TemplateSelectOption({
  option,
  selected,
  anchored,
  onSelect,
  onMouseEnter,
  onMouseLeave,
}: TemplateSelectOptionProps) {
  const { setNode, isTruncated } = useIsTruncated();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const button = (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="flex w-full min-w-0 items-start gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:bg-accent"
    >
      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
      <span ref={setNode} className="flex-1 line-clamp-3 break-words">
        {option.label}
      </span>
    </button>
  );

  // Unlike `PopoverTrigger`, `PopoverAnchor` writes no `data-state`, so it and the tooltip
  // trigger can share this button without fighting over it.
  const anchoredButton = anchored ? <PopoverAnchor asChild>{button}</PopoverAnchor> : button;

  // Mounted even when the name fits, and held shut through `open`. Returning the bare button
  // instead would change the element type here, remounting it on any resize that flips
  // truncation and dropping the focus of whoever was tabbed onto this row.
  return (
    <Tooltip open={isTruncated && isTooltipOpen} onOpenChange={setIsTooltipOpen}>
      <TooltipTrigger asChild>{anchoredButton}</TooltipTrigger>
      <TooltipContent className="max-w-sm break-words">{option.label}</TooltipContent>
    </Tooltip>
  );
}
